import { Chip, Typography } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid';
import type { FieldPath } from 'react-hook-form';
import { capitalize } from 'lodash';
import { RemoveUserButton } from './RemoveUserButton';
import { RoleDropdown } from './RoleDropdown';
import { EditableMemberNameCell } from './EditableMemberNameCell';
import type { RoleLabelValuePair } from '../members-table-types/role-label-value-pair';
import { type User, type Role, ROLES } from '@vy/types';
import { formatDate } from '../../../../../../../../utils/date';

export const MEMBERS_KEYS = Object.freeze({
  FIRST_NAME: 'firstName',
  LAST_NAME: 'lastName',
  EMAIL: 'email',
  STATUS: 'status',
  CREATED_AT: 'createdAt',
  ROLE: 'role',
  ACTIONS: 'actions',
} satisfies Record<string, FieldPath<User> | string>);

type GetMembersColumnsProps = {
  onChangeUserRole: (userId: string) => (labelValuePair: RoleLabelValuePair) => void;
  onChangeUserName: (userId: string, field: 'firstName' | 'lastName', value: string) => void;
};

export const getMembersColumns = ({
  onChangeUserRole,
  onChangeUserName,
}: GetMembersColumnsProps): GridColDef<User>[] => [
  {
    field: MEMBERS_KEYS.FIRST_NAME,
    headerName: 'First Name',
    sortable: false,
    renderCell: ({ row }) => (
      <EditableMemberNameCell
        value={row.firstName ?? ''}
        dataVyPrefix={`settings-members-first-name-${row.id}`}
        onCommit={(nextFirstName) => onChangeUserName(row.id, 'firstName', nextFirstName)}
      />
    ),
  },
  {
    field: MEMBERS_KEYS.LAST_NAME,
    headerName: 'Last Name',
    sortable: false,
    renderCell: ({ row }) => (
      <EditableMemberNameCell
        value={row.lastName ?? ''}
        dataVyPrefix={`settings-members-last-name-${row.id}`}
        onCommit={(nextLastName) => onChangeUserName(row.id, 'lastName', nextLastName)}
      />
    ),
  },
  {
    field: MEMBERS_KEYS.EMAIL,
    headerName: 'Member',
    sortable: false,
    renderCell: ({ row }) => (
      <Typography noWrap title={row.email}>
        {row.email}
      </Typography>
    ),
  },
  {
    field: MEMBERS_KEYS.STATUS,
    headerName: 'Status',
    sortable: false,
    renderCell: () => <Chip label="Active" color="success" size="small" variant="outlined" />,
  },

  {
    field: MEMBERS_KEYS.ROLE,
    headerName: 'Role',
    sortable: false,
    headerClassName: 'pl-8',
    valueGetter: (_value, row) => capitalize(row.role),
    renderCell: ({ row }) => (
      <RoleDropdown
        options={Object.values(ROLES).map(mapRoleToLabelValuePair)}
        defaultRole={mapRoleToLabelValuePair(row.role)}
        onChange={onChangeUserRole(row.id)}
      />
    ),
  },
  {
    field: MEMBERS_KEYS.CREATED_AT,
    headerName: 'Date Joined',
    sortable: false,
    renderCell: ({ row }) => <Typography>{formatDate(row.createdAt)}</Typography>,
  },
  {
    field: MEMBERS_KEYS.ACTIONS,
    headerName: '',
    sortable: false,
    renderCell: ({ row }) => <RemoveUserButton userId={row.id} userEmail={row.email} userRole={row.role} />,
  },
];

function mapRoleToLabelValuePair(role: Role): RoleLabelValuePair {
  return { label: capitalize(role), role };
}
